import { compact, groupBy } from 'lodash';

import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import {
  BUSINESS_ENTITY_KINDS,
  TRUST_ENTITY_KINDS,
} from '@/modules/entities/entities.constants';
import { getHoldingOwnerEntityIDFromSuggestion } from '@/modules/estate/estateAIOnboarding/estateOnboarding.utils';
import { AiSuggestionKind } from '@/types/schema';
import { pluralize } from '@/utils/formatting/strings';

import type { ReviewAiSuggestionsFromNotification_AiSuggestionFragment } from './graphql/ReviewAISuggestionsFromNotification.generated';

interface SuggestionCounts {
  individualsCount: number;
  entitiesCount: number;
  trustsCount: number;
  businessesCount: number;
  testamentaryEntitiesCount: number;
  organizationsCount: number;
  holdingsCount: number;
}

interface TabDescriptions {
  peopleTabDescription: string;
  trustsTabDescription: string;
  businessesTabDescription: string;
  entitiesTabDescription: string;
  holdingsTabDescription: string;
  peopleSavedAutomaticallyCount: number;
  trustsSavedAutomaticallyCount: number;
  businessesSavedAutomaticallyCount: number;
  holdingsSavedAutomaticallyCount: number;
}

const EMPTY_STATE_DESCRIPTION = 'Nothing to review';

/**
 * Groups suggestions by kind and calculates counts for each type
 */
function getSuggestionCounts(
  suggestions: ReviewAiSuggestionsFromNotification_AiSuggestionFragment[]
): SuggestionCounts {
  const suggestionsByKind = groupBy(suggestions, (s) => s.kind) as Record<
    AiSuggestionKind,
    ReviewAiSuggestionsFromNotification_AiSuggestionFragment[]
  >;

  const assetSuggestions = suggestionsByKind[AiSuggestionKind.Asset] || [];
  const liabilitySuggestions =
    suggestionsByKind[AiSuggestionKind.Liability] || [];
  const lifeInsuranceSuggestions =
    suggestionsByKind[AiSuggestionKind.LifeInsurance] || [];

  const assetCount = assetSuggestions.filter((suggestion) =>
    Boolean(getHoldingOwnerEntityIDFromSuggestion(suggestion))
  ).length;

  const liabilityCount = liabilitySuggestions.filter((suggestion) =>
    Boolean(getHoldingOwnerEntityIDFromSuggestion(suggestion))
  ).length;

  const lifeInsuranceCount = lifeInsuranceSuggestions.filter((suggestion) =>
    Boolean(getHoldingOwnerEntityIDFromSuggestion(suggestion))
  ).length;

  const holdingsCount = assetCount + liabilityCount + lifeInsuranceCount;

  const onboardingEntitySuggestions =
    suggestionsByKind[AiSuggestionKind.OnboardingEntity] || [];

  const aggregated = onboardingEntitySuggestions.reduce(
    (acc, s) => {
      const kind = s.onboardingEntity?.entityKind;
      if (kind && TRUST_ENTITY_KINDS.includes(kind)) {
        acc.trustsCount += 1;
      } else if (kind && BUSINESS_ENTITY_KINDS.includes(kind)) {
        acc.businessesCount += 1;
      }
      return acc;
    },
    { trustsCount: 0, businessesCount: 0 }
  );

  const trustsCount = aggregated.trustsCount;
  const businessesCount = aggregated.businessesCount;
  const entitiesCount = onboardingEntitySuggestions.length;

  return {
    individualsCount:
      suggestionsByKind[AiSuggestionKind.OnboardingIndividual]?.length || 0,
    entitiesCount,
    trustsCount,
    businessesCount,
    testamentaryEntitiesCount:
      suggestionsByKind[AiSuggestionKind.OnboardingTestamentaryEntity]
        ?.length || 0,
    organizationsCount:
      suggestionsByKind[AiSuggestionKind.OnboardingOrganization]?.length || 0,
    holdingsCount,
  };
}

/**
 * Generates tab descriptions based on suggestion counts and loading state
 */
export function getAISuggestionTabDescriptions(
  suggestions: ReviewAiSuggestionsFromNotification_AiSuggestionFragment[],
  loading: boolean,
  autoAcceptedSuggestions: ReviewAiSuggestionsFromNotification_AiSuggestionFragment[] = []
): TabDescriptions {
  if (loading) {
    return {
      peopleTabDescription: EMPTY_CONTENT_HYPHEN,
      trustsTabDescription: EMPTY_CONTENT_HYPHEN,
      businessesTabDescription: EMPTY_CONTENT_HYPHEN,
      entitiesTabDescription: EMPTY_CONTENT_HYPHEN,
      holdingsTabDescription: EMPTY_CONTENT_HYPHEN,
      peopleSavedAutomaticallyCount: 0,
      trustsSavedAutomaticallyCount: 0,
      businessesSavedAutomaticallyCount: 0,
      holdingsSavedAutomaticallyCount: 0,
    };
  }

  const applyReviewSuffix = (description: string) => `${description} to review`;

  const counts = getSuggestionCounts(suggestions);
  const autoAcceptedCounts = getSuggestionCounts(autoAcceptedSuggestions);

  const hasPendingPeople = Boolean(
    counts.individualsCount || counts.organizationsCount
  );
  const pendingTrustsCount =
    counts.trustsCount + counts.testamentaryEntitiesCount;

  const peopleSavedAutomaticallyCount = !hasPendingPeople
    ? autoAcceptedCounts.individualsCount +
      autoAcceptedCounts.organizationsCount
    : 0;
  const trustsSavedAutomaticallyCount = !pendingTrustsCount
    ? autoAcceptedCounts.trustsCount +
      autoAcceptedCounts.testamentaryEntitiesCount
    : 0;
  const businessesSavedAutomaticallyCount = !counts.businessesCount
    ? autoAcceptedCounts.businessesCount
    : 0;
  const holdingsSavedAutomaticallyCount = !counts.holdingsCount
    ? autoAcceptedCounts.holdingsCount
    : 0;

  const peopleTabDescription = (() => {
    if (!hasPendingPeople) {
      return EMPTY_STATE_DESCRIPTION;
    }

    const individualPluralized = pluralize({
      count: counts.individualsCount,
      singular: 'individual',
      plural: 'individuals',
    });

    const organizationPluralized = pluralize({
      count: counts.organizationsCount,
      singular: 'organization',
      plural: 'organizations',
    });

    return applyReviewSuffix(
      compact([
        counts.individualsCount && individualPluralized,
        counts.organizationsCount && organizationPluralized,
      ]).join(' & ')
    );
  })();

  const trustsTabDescription = (() => {
    if (!pendingTrustsCount) {
      return EMPTY_STATE_DESCRIPTION;
    }

    const trustPluralized = pluralize({
      count: counts.trustsCount,
      singular: 'trust',
      plural: 'trusts',
    });

    const testamentaryEntityPluralized = pluralize({
      count: counts.testamentaryEntitiesCount,
      singular: 'sub-trust',
      plural: 'sub-trusts',
    });

    return applyReviewSuffix(
      compact([
        counts.trustsCount && trustPluralized,
        counts.testamentaryEntitiesCount && testamentaryEntityPluralized,
      ]).join(' & ')
    );
  })();

  const businessesTabDescription = (() => {
    if (!counts.businessesCount) {
      return EMPTY_STATE_DESCRIPTION;
    }

    return applyReviewSuffix(
      pluralize({
        count: counts.businessesCount,
        singular: 'business',
        plural: 'businesses',
      })
    );
  })();

  const entitiesTabDescription = (() => {
    const combinedCount = counts.trustsCount + counts.testamentaryEntitiesCount;
    if (!combinedCount) {
      return EMPTY_STATE_DESCRIPTION;
    }

    const trustPluralized = pluralize({
      count: counts.trustsCount,
      singular: 'trust',
      plural: 'trusts',
    });

    const testamentaryEntityPluralized = pluralize({
      count: counts.testamentaryEntitiesCount,
      singular: 'sub-trust',
      plural: 'sub-trusts',
    });

    return applyReviewSuffix(
      compact([
        counts.trustsCount && trustPluralized,
        counts.testamentaryEntitiesCount && testamentaryEntityPluralized,
      ]).join(' & ')
    );
  })();

  const holdingsTabDescription = (() => {
    if (!counts.holdingsCount) {
      return EMPTY_STATE_DESCRIPTION;
    }
    return applyReviewSuffix(
      pluralize({
        count: counts.holdingsCount,
        singular: 'holding',
        plural: 'holdings',
      })
    );
  })();

  return {
    peopleTabDescription,
    trustsTabDescription,
    businessesTabDescription,
    entitiesTabDescription,
    holdingsTabDescription,
    peopleSavedAutomaticallyCount,
    trustsSavedAutomaticallyCount,
    businessesSavedAutomaticallyCount,
    holdingsSavedAutomaticallyCount,
  };
}
